import { useEffect, useRef } from "react";
import {
  groomingRunnerEventSchema,
  type GroomingRunnerEvent,
  type GroomingToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { getToolboxBridge, useToolsetSupport, type ToolsetSupport } from "@/lib/toolbox/bridge";

/**
 * The grooming runner: the half of the SpecTree Toolbox bridge that runs
 * Claude Code on the person's computer (epic PI-SPEC-SW-E279, task
 * PI-SPEC-SW-1390-1).
 *
 * The grooming pages are the same in a browser and in the app ("one review
 * screen, two places"). Only the run actions need the app: batches, one-item
 * grooming, the product-area draft, author questions and the repository
 * copies. Those actions are enabled only when the app ships the grooming
 * toolset AND its contract number is inside the range this SpecTree serves;
 * otherwise the page shows "Open in SpecTree Toolbox" guidance and, where
 * one exists, the in-session slash command that does the same for one item.
 *
 * The bridge and event types come from `@ttc-spectree/toolbox-contract`;
 * never re-type them here.
 */

/** The grooming runner when `support` is `supported`, else null. */
export function getRunnerBridge(support: ToolsetSupport | undefined): GroomingToolsetBridge | null {
  if (support !== "supported") return null;
  return getToolboxBridge()?.grooming ?? null;
}

export interface RunnerBridgeState {
  /** The runner, or null when run actions must stay disabled. */
  runner: GroomingToolsetBridge | null;
  /** Why: `browser`, an update needed on either side, or `undefined` while checking. */
  support: ToolsetSupport | undefined;
}

/** The grooming runner for a component, with the support state that decided it. */
export function useRunnerBridge(): RunnerBridgeState {
  const support = useToolsetSupport("grooming");
  return { runner: getRunnerBridge(support), support };
}

/** Plain words for why run actions are disabled; null when they are enabled. */
export function runnerUnavailableReason(support: ToolsetSupport | undefined): string | null {
  switch (support) {
    case "supported":
      return null;
    case undefined:
      return "Checking SpecTree Toolbox…";
    case "browser":
      return "Runs Claude Code on your computer, so it works only in the SpecTree Toolbox desktop app.";
    case "app-update-needed":
      return "Update SpecTree Toolbox to use this.";
    case "spectree-update-needed":
      return "SpecTree Toolbox is newer than this SpecTree. Ask your SpecTree admin to update SpecTree.";
  }
}

/**
 * Subscribe to the runner's events while mounted. Events cross the app/page
 * version boundary, so each is parsed with the contract's schema and one the
 * page cannot read is dropped.
 */
export function useRunnerEvents(
  runner: GroomingToolsetBridge | null,
  onEvent: (event: GroomingRunnerEvent) => void
): void {
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!runner) return undefined;
    return runner.onEvent((raw: unknown) => {
      const parsed = groomingRunnerEventSchema.safeParse(raw);
      if (parsed.success) handler.current(parsed.data);
    });
  }, [runner]);
}
